import { getRowRecordIds, type ViewQueryRecordNodeWithId } from '@salescore/core'
import { HashMap, Number, Option, pipe } from 'effect'
import * as React from 'react'
import { Fragment, type ReactElement, useMemo } from 'react'
import { useRecoilValue } from 'recoil'

import type { RSheetRecordNode, UnionOfRSheetColumn } from '../../../domain/column'
import type { RSheetsCursorPosition } from '../../../domain/cursor/cursorTypes'
import type { LatestFieldChangeItem } from '../../../recoil/recordsHistory'
import { rowConfigsImplAtom } from '../../../recoil/rowConfig'
import type { SetMainCursorByRecordIdArguments } from '../../hooks/useSetMainCursorByRecordId'
import { clampRowInnerRowSpan } from '../../util/clampRowInnerRowSpan'
import { RSheetsStyle } from '../../util/RSheetsStyle'

export type RenderCellGroup<T> = (properties: {
  row: ViewQueryRecordNodeWithId
  column: T
  rowIndex: number
  columnIndex: number
  expandable: boolean
  expanded: boolean
  latestFieldChangesRow: HashMap.HashMap<string, LatestFieldChangeItem>
  setMainCursorByRecordId: (arguments_: SetMainCursorByRecordIdArguments) => void
  setDragging: () => void
  expandOnDrag: (position: RSheetsCursorPosition) => void
}) => ReactElement

const RSheetRow = <T extends UnionOfRSheetColumn<RSheetRecordNode>>({
  targetColumns,
  row,
  rowIndex,
  columnIndexStart,
  rowHeight,
  setMainCursorByRecordId,
  getLatestFieldChanges,
  setDragging,
  expandOnDrag,
  visibleRowNumber,
  rowExpandable,
  renderCellGroup,
}: {
  targetColumns: T[] // このtargetColumnsはfilteredなケースがあるので、atomを使うのはNG
  row: ViewQueryRecordNodeWithId
  rowIndex: number
  columnIndexStart: number
  rowHeight: number
  setMainCursorByRecordId: (arguments_: SetMainCursorByRecordIdArguments) => void
  getLatestFieldChanges: (recordIds: string[]) => HashMap.HashMap<string, LatestFieldChangeItem>
  setDragging: () => void
  expandOnDrag: (position: RSheetsCursorPosition) => void
  visibleRowNumber: number | undefined
  rowExpandable: (row: ViewQueryRecordNodeWithId) => boolean
  renderCellGroup: RenderCellGroup<T>
}): ReactElement => {
  const rowConfigMap = useRecoilValue(rowConfigsImplAtom)
  const expanded = useMemo(
    () =>
      pipe(
        HashMap.get(rowConfigMap, row.id),
        Option.map((rowConfig) => rowConfig.expanded),
        Option.getOrElse(() => false),
      ),
    [rowConfigMap, row.id],
  )
  const expandable = useMemo(() => rowExpandable(row), [rowExpandable, row])
  // 現状、非固定化列の場合にしか参照されないのでそれを利用してしまっている。本当は width とかいう名前にして固定化列か否かで値を分岐させるべき
  const width = useMemo(() => Number.sumAll(targetColumns.map((x) => x.width)), [targetColumns])
  const latestFieldChangesRow = useMemo(() => getLatestFieldChanges(getRowRecordIds(row)), [getLatestFieldChanges, row])

  return (
    // Row
    <div
      // eslint-disable-next-line better-tailwindcss/no-unknown-classes -- rsheets-row は main.less にスタイル定義あり
      className={`rsheets-row`}
      data-e2e={`rsheets-row-${rowIndex}`}
      style={{
        minWidth: '100%',
        width,
        display: 'flex',
        height: clampRowInnerRowSpan({ record: row, expanded, visibleRowNumber }) * rowHeight,
        overflow: 'hidden',
        borderBottom: `1px ${RSheetsStyle.color.borderBlack} solid`,
        // borderRight: isFixed ? RSheetsStyle.border.rightEdgeColumn : undefined,
      }}
    >
      {/* column */}
      {targetColumns.map((column, index) => {
        const columnIndex = index + columnIndexStart
        return (
          <Fragment key={column.key}>
            {renderCellGroup({
              row,
              column,
              rowIndex,
              columnIndex,
              expandable,
              expanded,
              latestFieldChangesRow,
              setMainCursorByRecordId,
              setDragging,
              expandOnDrag,
            })}
          </Fragment>
        )
      })}
    </div>
  )
}

// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- React.memoを通すと型推論が壊れるので仕方がない。
export const RSheetRowMemo = React.memo(RSheetRow) as unknown as <T extends UnionOfRSheetColumn<RSheetRecordNode>>(
  properties: React.ComponentProps<typeof RSheetRow<T>>,
) => ReactElement
